import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Drive.css';

const formatSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
};

const MAX_FILE_BYTES = 200 * 1024 * 1024;

const Drive = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { fetchFiles(); }, []);

  const fetchFiles = async () => {
    const res = await fetch('/privateZone/drive_list.php', { credentials: 'include' });
    if (res.status === 401) { navigate('/login'); return; }
    const data = await res.json();
    setFiles(data.files);
    setTotalSize(data.total);
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    if (file.size > MAX_FILE_BYTES) {
      setError('Fichier trop volumineux (max 200 MB)');
      return;
    }

    setError('');
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.withCredentials = true;
    xhr.upload.addEventListener('progress', (ev) => {
      if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
    });
    xhr.onload = () => {
      setUploading(false);
      if (xhr.status === 200) {
        fetchFiles();
      } else {
        try {
          const data = JSON.parse(xhr.responseText);
          setError(data.error || 'Erreur upload');
        } catch {
          setError('Erreur upload');
        }
      }
    };
    xhr.onerror = () => { setUploading(false); setError('Erreur réseau'); };
    xhr.open('POST', '/privateZone/drive_upload.php');
    xhr.send(formData);
  };

  const handleDownload = (filename) => {
    window.open(`/privateZone/drive_download.php?file=${encodeURIComponent(filename)}`);
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Supprimer "${filename}" ?`)) return;
    const res = await fetch('/privateZone/drive_delete.php', {
      method: 'DELETE',
      credentials: 'include',
      body: JSON.stringify({ file: filename }),
    });
    if (res.ok) fetchFiles();
  };

  return (
    <div className="drive-container">
      <div className="drive-header">
        <button className="drive-back" onClick={() => navigate('/private/privatePage')}>← Retour</button>
        <h2>Drive</h2>
        <span className="drive-used">{formatSize(totalSize)}</span>
      </div>

      <div className="drive-upload-zone">
        <button
          className="drive-upload-btn"
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          {uploading ? `Envoi... ${progress}%` : '+ Ajouter un fichier'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleUpload}
        />
        {uploading && (
          <div className="drive-progress-bar">
            <div className="drive-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        )}
        {error && <span className="drive-error">{error}</span>}
      </div>

      <ul className="drive-file-list">
        {files.length === 0 && <li className="drive-empty">Aucun fichier</li>}
        {files.map((f) => (
          <li key={f.name} className="drive-file-item">
            <div className="drive-file-info">
              <span className="drive-file-name" title={f.name}>{f.name}</span>
              <span className="drive-file-meta">{formatSize(f.size)} · {f.date}</span>
            </div>
            <div className="drive-file-actions">
              <button onClick={() => handleDownload(f.name)} title="Télécharger">↓</button>
              <button className="drive-delete-btn" onClick={() => handleDelete(f.name)} title="Supprimer">✕</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Drive;
